// src/app/pages/app/TradePage.jsx
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import TradeTicket from "../../features/trade/TradeTicket.jsx";
import OrderBook from "../../components/trading/OrderBook.jsx";
import LivePriceChart from "../../components/charts/LivePriceChart.jsx";
import { useMarket } from "../../features/markets/marketContext.jsx";
import { ASSET_CLASSES } from "../../features/markets/marketCatalog.js";
import { getWalletOverview } from "../../features/wallet/walletApi.js";
import {
  CandlestickChart,
  Coins,
  Globe,
  Gem,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
  List,
  X,
  AlertCircle,
  Sparkles,
  TrendingUp,
  Award,
  Zap,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";

const ASSET_TABS = [
  { key: ASSET_CLASSES.CRYPTO, label: "Crypto", icon: Coins, description: "Trade major cryptocurrencies using live exchange data." },
  { key: ASSET_CLASSES.FX, label: "FX", icon: Globe, description: "Trade fiat-to-USDT spot markets where available (live exchange pricing)." },
  { key: ASSET_CLASSES.METALS, label: "Metals", icon: Gem, description: "Trade metals proxies using live markets (e.g., PAXG for gold)." },
];

function TabButton({ active, onClick, icon: Icon, children, description }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl border text-sm font-semibold transition-all duration-200",
        active
          ? "border-indigo-500/40 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 text-white shadow-lg shadow-indigo-500/20"
          : "border-gray-800 bg-gray-900/30 text-gray-400 hover:border-gray-700 hover:text-white hover:bg-gray-800/50",
      ].join(" ")}
      title={description}
    >
      <Icon size={18} className={active ? "text-indigo-400" : ""} />
      {children}
    </button>
  );
}

function MarketButton({ active, onClick, market }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "w-full text-left px-4 py-3 rounded-xl border text-sm transition-all duration-200 group",
        active
          ? "border-indigo-500/40 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 text-white shadow-md shadow-indigo-500/10"
          : "border-gray-800 bg-gray-900/20 text-gray-400 hover:border-gray-700 hover:text-white hover:bg-gray-800/30",
      ].join(" ")}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={[
            "h-9 w-9 rounded-xl flex items-center justify-center text-white font-bold transition-all duration-200",
            active 
              ? "bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/25" 
              : "bg-gradient-to-br from-gray-700 to-gray-600 group-hover:from-indigo-500/50 group-hover:to-purple-500/50"
          ]}>
            {market.icon}
          </div>
          <div>
            <div className="font-semibold">{market.label}</div>
            <div className="text-xs text-gray-500">
              {market.base}/{market.quote}
            </div>
          </div>
        </div>
        {active && (
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400">Live</span>
          </div>
        )}
      </div>
    </button>
  );
}

export default function TradePage() {
  const { assetClass, setAssetClass, marketId, setMarketId, markets, currentMarket, symbol: backendSymbol } = useMarket();

  const [showOrderBook, setShowOrderBook] = useState(false);
  const [mobileMarketsOpen, setMobileMarketsOpen] = useState(false);

  const chartHeight = useMemo(() => 340, []);

  // ✅ Fetch wallet balance to display available balance
  const { data: walletData, isLoading: loadingBalance } = useQuery({
    queryKey: ["walletOverview"],
    queryFn: getWalletOverview,
    staleTime: 5000,
    refetchInterval: 10000,
  });

  // ✅ Calculate available balance for trading (USDT on TRON)
  const availableBalance = useMemo(() => {
    if (!walletData?.balances) return 0;
    const usdtBalance = walletData.balances.find(
      (b) => b.asset === "USDT" && b.network === "TRON"
    );
    return usdtBalance ? Number(usdtBalance.available || 0) : 0;
  }, [walletData]);

  const classLabel =
    assetClass === ASSET_CLASSES.CRYPTO
      ? "Cryptocurrency Trading"
      : assetClass === ASSET_CLASSES.FX
      ? "FX Trading (Spot FX via USDT pairs)"
      : "Metals Trading (Live proxy markets)";

  const classDesc =
    assetClass === ASSET_CLASSES.CRYPTO
      ? "Trade crypto markets with continuously updating exchange pricing."
      : assetClass === ASSET_CLASSES.FX
      ? "Trade fiat-to-USDT spot markets where available (e.g., EURUSDT, GBPUSDT) using live exchange pricing."
      : "Trade metals proxies such as PAXGUSDT (gold-backed) using live exchange pricing.";

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 space-y-6">
      {/* Header - Enhanced */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <CandlestickChart size={20} className="text-white" />
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              Trade
              <span className="text-xs font-medium bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Live Markets
              </span>
            </h1>
            <p className="text-sm text-gray-400">Real-time trading across crypto, FX & metals</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Live Status Indicator */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400 font-medium">Live Data</span>
          </div>

          <button
            type="button"
            onClick={() => setMobileMarketsOpen(true)}
            className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-800 bg-gray-900/30 text-gray-300 hover:border-gray-700 hover:text-white transition-colors"
          >
            <List size={16} />
            Markets
          </button>

          <Link
            to="/app/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-800 bg-gray-900/30 text-gray-300 hover:border-gray-700 hover:text-white transition-colors"
          >
            <LayoutDashboard size={16} />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </div>
      </div>

      {/* Asset tabs - Enhanced */}
      <div className="flex flex-wrap items-center gap-2">
        {ASSET_TABS.map((tab) => (
          <TabButton
            key={tab.key}
            icon={tab.icon}
            active={assetClass === tab.key}
            onClick={() => setAssetClass(tab.key)}
            description={tab.description}
          >
            {tab.label}
          </TabButton>
        ))}
      </div>

      {/* Banner - Enhanced with glass effect */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-950/40 via-purple-950/30 to-cyan-950/30 border border-indigo-500/20 p-5 backdrop-blur-sm">
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl" />
        <div className="relative flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0 border border-indigo-500/20">
            {assetClass === ASSET_CLASSES.CRYPTO && <Coins size={20} className="text-indigo-400" />}
            {assetClass === ASSET_CLASSES.FX && <Globe size={20} className="text-blue-400" />}
            {assetClass === ASSET_CLASSES.METALS && <Gem size={20} className="text-yellow-400" />}
          </div>
          <div>
            <div className="font-semibold text-white flex items-center gap-2">
              {classLabel}
              <span className="text-xs font-medium text-indigo-300 bg-indigo-500/20 px-2 py-0.5 rounded-full">
                {assetClass}
              </span>
            </div>
            <div className="text-sm text-gray-400 mt-0.5">{classDesc}</div>
          </div>
        </div>
      </div>

      {/* Chart - Enhanced with better container */}
      <div className="relative">
        <div className="absolute -top-4 -right-4 w-20 h-20 bg-indigo-500/5 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-cyan-500/5 rounded-full blur-xl" />
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 shadow-2xl shadow-black/20">
          <LivePriceChart marketId={marketId} interval="1m" height={chartHeight} />
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Trade ticket - Enhanced */}
        <div className="col-span-12 lg:col-span-8">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 shadow-2xl shadow-black/20">
            {/* Decorative glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="p-5 sm:p-6 border-b border-gray-800/80">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        Create Trade
                        <Sparkles size={16} className="text-indigo-400" />
                      </h2>
                      {currentMarket && (
                        <>
                          <span className="px-3 py-1 rounded-lg text-sm font-medium bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/20 text-indigo-300">
                            {currentMarket.label}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                            {assetClass}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Choose delivery time, direction, account type, and amount
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowOrderBook((s) => !s)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-800 bg-gray-900/30 text-gray-300 hover:border-gray-700 hover:text-white transition-colors w-full sm:w-auto"
                  >
                    {showOrderBook ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showOrderBook ? "Hide Order Book" : "View Order Book"}
                  </button>
                </div>

                {/* ✅ Updated Info Cards - Removed Max Trade, showing Available Balance instead */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border border-amber-500/20">
                    <div className="flex items-center gap-2">
                      <Zap size={14} className="text-amber-400" />
                      <span className="text-xs text-gray-300">Demo Mode</span>
                    </div>
                    <p className="text-sm font-semibold text-yellow-300 mt-0.5">Projected payouts</p>
                  </div>
                  <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/5 border border-indigo-500/20">
                    <div className="flex items-center gap-2">
                      <Award size={14} className="text-indigo-400" />
                      <span className="text-xs text-gray-300">Platform Fee</span>
                    </div>
                    <p className="text-sm font-semibold text-white mt-0.5">0.5% on stake</p>
                  </div>
                </div>

                {/* ✅ Balance reminder - Shows available balance dynamically */}
                <div className="mt-3 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
                  <div className="flex items-start gap-2 text-xs text-blue-300/80">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-blue-400" />
                    <span>Trades are limited by your available balance. Current balance: <span className="font-semibold text-white">{loadingBalance ? "..." : `${availableBalance.toFixed(2)} USDT`}</span></span>
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6">
                <TradeTicket selectedMarket={currentMarket?.id || marketId} assetClass={assetClass} />
              </div>

              {showOrderBook && (
                <div className="border-t border-gray-800">
                  <div className="p-4 bg-gray-900/40">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                      <div className="text-sm font-semibold text-white">Order Book</div>
                      <div className="text-xs text-gray-400">
                        Live order book for {currentMarket?.label || marketId}
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <OrderBook symbol={backendSymbol} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop markets list - Enhanced */}
        <div className="hidden lg:block col-span-12 lg:col-span-4">
          <div className="relative rounded-2xl overflow-hidden h-full bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 shadow-2xl shadow-black/20">
            <div className="p-5 border-b border-gray-800/80">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-white flex items-center gap-2">
                    Markets
                    <span className="text-xs text-gray-400">({markets.length})</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {assetClass.toLowerCase()} markets available
                  </div>
                </div>
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 flex items-center justify-center border border-indigo-500/20">
                  {assetClass === ASSET_CLASSES.CRYPTO && <Coins size={16} className="text-indigo-400" />}
                  {assetClass === ASSET_CLASSES.FX && <Globe size={16} className="text-blue-400" />}
                  {assetClass === ASSET_CLASSES.METALS && <Gem size={16} className="text-yellow-400" />}
                </div>
              </div>
            </div>

            <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-700">
              {markets.length > 0 ? (
                markets.map((market) => (
                  <MarketButton
                    key={market.id}
                    market={market}
                    active={marketId === market.id}
                    onClick={() => setMarketId(market.id)}
                  />
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-3">📊</div>
                  <div className="text-sm">No markets available</div>
                </div>
              )}
            </div>

            {/* ✅ Updated Trading Limits - "Maximum" line removed */}
            <div className="p-4 border-t border-gray-800/80 bg-gradient-to-b from-gray-900/50 to-gray-950/50">
              <div className="text-xs">
                <div className="font-semibold text-gray-400 mb-3 flex items-center gap-2">
                  <Award size={12} className="text-indigo-400" />
                  Trading Limits
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Minimum", value: "0.01 USDT", color: "text-gray-300" },
                    { label: "Platform Fee", value: "0.5%", color: "text-gray-300" },
                    { label: "Balance Check", value: "Available balance enforced", color: "text-emerald-300" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                      <span className="text-gray-400">{item.label}</span>
                      <span className={`font-medium ${item.color}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile markets drawer - Enhanced */}
      {mobileMarketsOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={() => setMobileMarketsOpen(false)} />
          <div className="fixed inset-y-0 right-0 z-50 w-[90vw] max-w-sm bg-gradient-to-b from-gray-900 to-gray-950 border-l border-gray-800/60 shadow-2xl">
            <div className="p-5 border-b border-gray-800 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">Markets</div>
                <div className="text-xs text-gray-400 mt-0.5">{markets.length} available</div>
              </div>
              <button
                type="button"
                onClick={() => setMobileMarketsOpen(false)}
                className="p-2 rounded-xl hover:bg-gray-800/60 text-gray-300 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-80px)]">
              {markets.map((m) => (
                <MarketButton
                  key={m.id}
                  market={m}
                  active={marketId === m.id}
                  onClick={() => {
                    setMarketId(m.id);
                    setMobileMarketsOpen(false);
                  }}
                />
              ))}
            </div>

            <div className="h-[env(safe-area-inset-bottom)]" />
          </div>
        </div>
      )}
    </div>
  );
}
